import { toast } from 'sonner';

import i18n from '../i18n';
import { ApiRequestError, isApiRequestError } from './api-error';

/**
 * The single place an error becomes a sentence on screen.
 *
 * The api never returns prose — it returns `{ code, params }` where `code` is
 * an i18n key (ARCHITECTURE §4). Resolution order is `common:errors.<CODE>`
 * (the shared catalog, in lockstep with `ERROR_CODES`) then
 * `shell:errors.<CODE>` (client-only codes like `E_NETWORK`). An unknown code
 * renders as the code itself — visible and greppable rather than silent.
 */

export function errorMessage(error: unknown): string {
  if (isApiRequestError(error)) {
    return translateCode(error.code, error.params);
  }
  if (error instanceof Error && error.message) return error.message;
  return translateCode('E_INTERNAL');
}

export function translateCode(code: string, params?: Record<string, unknown>): string {
  return i18n.t(`errors.${code}`, {
    ns: ['common', 'shell'],
    defaultValue: code,
    ...params,
  });
}

/**
 * Toast an api failure. Deduped by code+params so eight parallel queries dying
 * on one expired session produce ONE toast, not eight.
 */
export function toastError(error: unknown, options: { id?: string } = {}): void {
  // A 401 is handled by the auth layer (silent refresh → login redirect);
  // toasting it as well just shouts at a user who is already being redirected.
  if (isApiRequestError(error) && error.isUnauthorized) return;
  // Aborts are intentional (component unmounted, query superseded).
  if (error instanceof DOMException && error.name === 'AbortError') return;

  const message = errorMessage(error);
  const id = options.id ?? `err:${isApiRequestError(error) ? error.code : message}`;
  toast.error(message, { id });
}

export function toastSuccess(message: string, options: { id?: string; description?: string } = {}): void {
  toast.success(message, options);
}

export function toastInfo(message: string, options: { id?: string; description?: string } = {}): void {
  toast(message, options);
}

export { ApiRequestError, isApiRequestError };
